import tpl from './select.tpl.html';
import {IS_MOBILE, IS_TABLET} from '../../../app/common/device-info.utils';

// Counter for automatic ID creation
let autoid = 0;

/**
 * @typedef {{label: string}} MDCSelectOption
 */

class MDCSelectComponent {
  constructor($scope, $timeout, $element, $animate) {
    'ngInject';

    this.$scope = $scope;
    this.$timeout = $timeout;
    this.$element = $element;

    // Prevent animations as ng-class input will otherwise be delayed which in turn breaks width calculations
    // for the label span element.
    $animate.enabled(this.$element, false);

    /**
     * Label of the dropdown
     * @type {string}
     */
    this.label = '';

    /**
     * List of options, objects that have a key 'label' that is displayed as the name.
     * @type {Array.<MDCSelectOption>}
     */
    this.options = [];

    /**
     * Is the control disabled?
     * @type {boolean}
     */
    this.isDisabled = false;

    /**
     * Is the control required?
     * @type {boolean}
     */
    this.isRequired = false;

    this.extraClasses = null;

    /**
     * The model exposed to the outside.
     * @type {MDCSelectOption}
     */
    this.model = null;

    /**
     * ID of the select element. Only applies to mobile (native) select.
     * @type {string}
     */
    this.pId = `mdc-select-${++autoid}`;
  
    /**
     * ID of the select menu element as it is hoisted to body element.
     * @type {?string}
     */
    this.menuId = null;

    // The select element
    this._selectElem = null;

    // The MDCSelect object or native select element (both give a similar API for our usage)
    this._select = null;

    // Should we use native select instead? It is used for mobile devices.
    this._useNative = IS_MOBILE || IS_TABLET;
  }

  $onInit() {
    this.$timeout(() => {
      if (this._useNative) {
        this._selectElem = this.$element[0].getElementsByClassName('mdc-select-native')[0];
      }
      else {
        this._selectElem = this.$element[0].getElementsByClassName('mdc-select-js')[0];
      }
      
      this._select = new window.mdc.MDCSelect.attachTo(this._selectElem);
      this._select.listen('MDCSelect:change', (...args) => this._onSelectChangeEvent(...args));

      if (this.isDisabled) {
        this._select.disabled = this.isDisabled;
      }

      this._setSelected();

      this.$scope.$watch(() => this.model.$viewValue, () => this._setSelected());
    });
  }

  $onChanges(changes) {
    if ('isDisabled' in changes && typeof changes.isDisabled.currentValue === 'boolean' && this._select != null) {
      this._select.disabled = changes.isDisabled.currentValue;
    }
  }

  _onSelectChangeEvent() {
    // Use timeout as function was possibly launched from non-angularjs event
    this.$timeout(() => {
      const value = this.options[this._select.selectedIndex];
      this.model.$setTouched();
      this.model.$setViewValue(value);
      this._checkRequired();
      this.label = value.label;
    });
  }

  _setSelected() {
    if (this._select != null) {
      if (this.model.$viewValue != null) {
        this.$timeout(() => this._select.selectedIndex = this.options.findIndex(o => o === this.model.$viewValue));
      }
      else {
        this._select.selectedIndex = 0;
      }

      this._checkRequired();
    }
  }

  _checkRequired() {
    if (this.model.$viewValue == null && this.isRequired) {
      this.model.$setValidity('required', false);
    }
    else {
      this.model.$setValidity('required', true);
    }
  }
}

const config = {
  templateUrl: tpl,
  controller: MDCSelectComponent,
  bindings: {
    options: '<',
    label: '@',
    isDisabled: '<?',
    isRequired: '<?',
    extraClasses: '@?',
    pId: '@?',
    menuId: '@?'
  },
  require: {
    model: 'ngModel'
  }
};

const mod = angular.module('common.materialComponents.select', [])

.component('mdcSelect', config);

export default mod.name;
